// Taki listesinin SAF kurallari — Listing Builder (tek urun) ve Liste
// Denetimi (toplu) ayni fiyat, varyasyon ve kisisellestirme mantigini kullanir.
// Burada state yok: urun + sablon + secenek setleri verilir, taslak alanlari doner.

import type { ProductCategory, ProductInspiration } from "@erc/shared";
import { optionValueMatch, productPriceVaries, extractProductDimensions, DIMENSIONS_ATTRIBUTE, descriptionPlainText, etsyFeeBreakdown, etsyFeeRate } from "@erc/shared";
import { productJewelryType } from "./jewelryType";

/** Urun adi sinirlari: Trendyol paneli 100, Etsy 140 karakterde kesiyor */
export const TITLE_LIMITS: Record<string, number> = { trendyol: 100, etsy: 140 };
/** Pazaryeri komisyon varsayilanlari (%) — kullanici degistirebilir */
export const COMMISSION_DEFAULTS: Record<string, number> = { trendyol: 21, etsy: 6.5 };

export interface TrendyolAttr {
  attributeId: number;
  attributeName: string;
  attributeValueId?: number;
  customAttributeValue?: string;
}

/** Sablonun builder'i on dolduran kurallari — ListingTemplate.attributes icinde */
export interface TemplateRules {
  attributeSources?: Record<string, { source: "renk" | "beden" | "materyal"; name: string; allowCustom: boolean; varianter: boolean }>;
  titlePattern?: string;
  descriptionPattern?: string;
  priceMultiplier?: number;
  commissionPercent?: number;
  /** Aciklama ALT METNI: AI'nin yazdigi anahtar kelimeli girisin altina eklenir.
   *  Yer tutucular urunden doldurulur — bkz. renderDescriptionFooter. */
  descriptionFooter?: string;
  /** Kategorinin EN UCUZ varyasyonu (orn. Rose Gold + 12 inches) ve dusuk kar hedefi */
  entryVariant?: EntryVariant | null;
  /** Etsy: hedef net kar marji (% — net kar / satis). Fiyat bundan hesaplanir. */
  targetMarginPct?: number;
}

/** Varsayilan fiyat carpani: (maliyet + kargo) x 1.5 */
export const DEFAULT_PRICE_MULTIPLIER = 1.5;
/** Etsy'de %50 indirim yapilacagi icin gonderilen LISTE fiyati = satis fiyati x 2 */
export const LIST_PRICE_FACTOR = 2;

/** En ucuz varyasyon: eslesen kombinasyonlar dusuk kar hedefiyle fiyatlanir */
export interface EntryVariant {
  /** eksen adi -> deger (verilmeyen eksen "fark etmez") */
  values: Record<string, string>;
  /** Hedef net kar marji (%). Yoksa eski kayit: multiplier gecerli. */
  marginPct?: number;
  /** Eski kayitlar: (maliyet + kargo) x carpan */
  multiplier?: number;
}

// ── Etsy ucretleri ve hedef kara gore fiyat ──────────────────────────
// Ortak model (shared/etsy-fees): siparis ledger'indan olculen kalemler —
// islem %6.5, odeme %3 + $0.25, regulatory %1.68, bunlarin KDV'si %20,
// yenileme $0.20. Burada kargo ucretsiz varsayilir (satis = urun + kargo).
/** Etsy basligi: kelimelerin ilk harfi buyuk ("gift for her" -> "Gift for Her").
 *  Grubun ortasindaki baglaclar kucuk kalir; EKG/925 gibi degerlere dokunulmaz.
 *  Sunucudaki listing-content etsyTitleCase ile ayni kural. */
const TITLE_SMALL_WORDS = new Set(["a", "an", "and", "as", "at", "by", "for", "from", "in", "of", "on", "or", "the", "to", "with"]);
export function etsyTitleCase(title: string): string {
  return title.split(",").map((segment) => {
    let first = true;
    return segment.replace(/\S+/g, (word) => {
      const lower = word.toLowerCase();
      const keepSmall = !first && TITLE_SMALL_WORDS.has(lower);
      first = false;
      if (keepSmall) return lower;
      return word.replace(/^(\P{L}*)(\p{Ll})/u, (_, pre: string, ch: string) => pre + ch.toUpperCase());
    });
  }).join(",");
}

/** Varsayilan hedef net kar marji (%): x1.5 carpanina yakin sonuc verir */
export const DEFAULT_TARGET_MARGIN = 20;

/** En son girilen hedef kar (Liste Denetimi + taki builder ortak): yeni urunde
 *  varsayilan olur; urune ozel deger ondan once gelir, sablon ondan sonra */
const LAST_MARGIN_KEY = "erc:jewelry:last-margin";
export function readLastMargin(): number | null {
  try { const v = Number(localStorage.getItem(LAST_MARGIN_KEY)); return Number.isFinite(v) && v > 0 ? v : null; } catch { return null; }
}
export function writeLastMargin(v: number) {
  try { localStorage.setItem(LAST_MARGIN_KEY, String(v)); } catch { /* depolama kapali */ }
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const feeRate = () => etsyFeeRate().rate;
const feeFixed = () => etsyFeeRate().fixed;

/** Satis fiyatindan Etsy'nin kestigi ucretler (kalem kalem; listing = satista yenileme) */
export function etsyFees(sale: number) {
  const f = etsyFeeBreakdown(sale);
  return { ...f, listing: f.renew };
}

/** Kar analizi: net kar, marj (net / satis) ve maliyete gore oran (net / urun maliyeti) */
export function etsyProfit(sale: number, cost: number, shipping: number) {
  const fees = etsyFees(sale);
  const net = sale - cost - shipping - fees.total;
  return {
    fees, net,
    marginPct: sale > 0 ? (net / sale) * 100 : 0,
    markupPct: cost > 0 ? (net / cost) * 100 : 0,
  };
}

/** Fiyat kurali: (maliyet + kargo) -> satis, ve tersi (en ucuz varyasyonda geri gitmek icin) */
export interface PriceRule {
  price(base: number): number;
  base(sale: number): number;
  label: string;
}

/** Eski kural: satis = (maliyet + kargo) x carpan. Trendyol ve eski taslaklar. */
export function multiplierRule(m: number): PriceRule {
  const k = m > 0 ? m : 1;
  return { price: (b) => r2(b * k), base: (s) => s / k, label: `×${k}` };
}

/** Etsy: tum ucretler dusuldukten sonra satisin marginPct'i net kar kalir.
 *  satis = (maliyet + kargo + sabit ucretler) / (1 - ucret orani - marj) */
export function marginRule(marginPct: number): PriceRule {
  const denom = Math.max(0.05, 1 - feeRate() - marginPct / 100);
  return {
    price: (b) => r2((b + feeFixed()) / denom),
    base: (s) => s * denom - feeFixed(),
    label: `%${marginPct} kâr`,
  };
}

/** En ucuz varyasyonun fiyat kurali (eski kayitlarda carpan) */
export function entryRule(entry: EntryVariant): PriceRule {
  return entry.marginPct != null ? marginRule(entry.marginPct) : multiplierRule(entry.multiplier ?? 1.2);
}

export interface TemplateRow {
  id: string;
  name: string;
  description: string;
  price: number | null;
  quantity: number;
  category: string | null;
  trendyolCategoryId: number | null;
  trendyolAttributes: TrendyolAttr[] | null;
  attributes: TemplateRules | null;
  optionRefs?: OptionRef[] | null;
  tags?: string[] | null;
  // Etsy'ye yukleme icin zorunlu alanlar sablondan gelir
  shippingProfileId?: string | null;
  taxonomyId?: number | null;
  // Taki tarifi: urun grubu, uretilecek sahneler ve fiyat kurali sablonda durur
  productTypeKey?: string | null;
  conceptCollectionId?: string | null;
  priceMultiplier?: number | null;
  // Sablonun diger Etsy alanlari (Liste Denetimi'ndeki sablon penceresi icin)
  materials?: string[] | null;
  /** Sablonun kendi gorselleri (olcu/renk tablosu vb.) — liste gorsellerinin sonuna */
  imageUrls?: string[] | null;
  whoMade?: string | null;
  whenMade?: string | null;
  whatIsIt?: string | null;
  isSupply?: boolean | null;
  listingType?: string | null;
  autoRenew?: boolean | null;
  isFeatured?: boolean | null;
  processingProfileId?: string | null;
  productionPartnerId?: string | null;
  returnPolicyId?: string | null;
  sectionId?: string | null;
  sku?: string | null;
}

/** Builder'da uretilen ve duzenlenebilen varyasyon ekseni */
export interface PlannedOption {
  label: string;
  priceAdjust?: number;
  sellingPrice?: number;
  sku?: string;
  /** Listeye dahil mi — kullanici tek tek cikarabilir */
  included?: boolean;
}
export interface PlannedVariation {
  name: string;
  options: PlannedOption[];
}

/** Secenek kutuphanesindeki set (renk, zincir uzunlugu, dogum tasi, isim…) */
export interface OptionSet {
  id: string;
  name: string;
  etsyName?: string | null;
  chartImageUrl?: string | null;
  kind: "list" | "text" | "upload" | "chart";
  values: Array<{ label: string }>;
  defaultRole: "variation" | "personalization";
  maxChars: number | null;
  /** Metin alaninda aliciya gosterilen yonerge */
  note?: string | null;
  /** Set belirli bir urun turune ait (kolye/bileklik zincir uzunlugu) */
  productTypeKey?: string | null;
}

/** Sablonun secenek referansi: hangi set, hangi rolde */
export interface OptionRef {
  optionSetId: string;
  role: "variation" | "personalization";
  required?: boolean;
  /** Urune ozel: yalnizca bu degerler (bos: setin tamami) */
  values?: string[];
  /** true: set yalnizca CHART gorseli icin bagli (fotograflarin sonuna eklenir);
   *  soru/varyasyon olarak sorulmaz (or. coklu tasli urunde doğum tasi tablosu) */
  chartOnly?: boolean;
  /** Ucretli istege bagli ek (arka yuz kazima): tedarikci ek maliyeti, USD */
  addOnCost?: number;
}

/** Tedarikci aciklamasindaki editor artiklarini temizler: inline style,
 *  contenteditable isaretleri ve bos span'lar. Trendyol aciklamayi HTML
 *  olarak alir ama stil tasiyan etiketleri kirpiyor; temizi gonderiyoruz. */
export function cleanSupplierHtml(html: string): string {
  return html
    .replace(/\s(?:style|class|contenteditable|data-list)="[^"]*"/gi, "")
    .replace(/<span>\s*<\/span>/gi, "")
    .replace(/<span>/gi, "").replace(/<\/span>/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** {urun} {marka} {materyal} {renk} gibi degiskenleri doldurur */
export function applyPattern(pattern: string, vars: Record<string, string>): string {
  return pattern.replace(/\{(\w+)\}/g, (_m, k: string) => vars[k] ?? "").replace(/\s{2,}/g, " ").trim();
}

/** Urunun SABIT ozellikleri (tek degerli eksenler: Materyal, Zincir Tipi,
 *  Zincir Uzunlugu…) — baslik/aciklama kaliplarinda {ozellikler}, Etsy
 *  malzemesi ve AI icerik icin. */
export function productAttributes(product: ProductCategory | null | undefined): Record<string, string> {
  const out = Object.fromEntries(Object.entries(product?.source?.attributes ?? {})
    .filter(([k, v]) => k && v && !/bask|print/i.test(k))) as Record<string, string>;
  // Olcu: urunde kayitli olcu, yoksa aciklamadan okunan (eski kayitlar icin)
  if (!out[DIMENSIONS_ATTRIBUTE]) {
    // AI'nin fotograftan TAHMINI (estimated) yalniz gorsel uretimi icindir; aciklamaya gercek olcu gibi yazilmaz
    const ps = product?.physicalSize?.estimated ? undefined : product?.physicalSize;
    const d = ps && (ps.widthMm || ps.heightMm) ? ps : extractProductDimensions(product?.source?.descriptionHtml || product?.source?.descriptionText);
    if (d?.widthMm || d?.heightMm) out[DIMENSIONS_ATTRIBUTE] = d.widthMm && d.heightMm ? `${d.widthMm} × ${d.heightMm} mm` : `${d.widthMm ?? d.heightMm} mm`;
  }
  return out;
}
export function attributeLines(product: ProductCategory | null | undefined): string {
  return Object.entries(productAttributes(product)).map(([k, v]) => `• ${k}: ${v}`).join("\n");
}
/** Etsy "materials" alani: Materyal/Material ozelligi */
export function productMaterials(product: ProductCategory | null | undefined): string[] {
  const a = productAttributes(product);
  const m = a["Materyal"] ?? a["Material"] ?? a["Malzeme"];
  return m ? [m.slice(0, 45)] : [];
}

/** Maliyet: pazaryerinin ulkesindeki fiyat (Etsy: ABD, Trendyol: TR) */
export function productCost(product: ProductCategory | null | undefined, isEtsy: boolean): number {
  const s = product?.sizeOptions?.[0];
  if (!s) return 0;
  const tiers = (isEtsy ? s.byCountry?.US?.costTiers : s.byCountry?.TR?.costTiers) ?? s.costTiers;
  return tiers?.premium ?? tiers?.plus ?? tiers?.base ?? s.cost ?? 0;
}

/** Etsy'de varsayilan kargo maliyeti (USD) — urunde ayrica girilmemisse */
export const DEFAULT_ETSY_SHIPPING_USD = 8.7;

/** Kargo maliyeti. Etsy: urune girilen tutar, yoksa varsayilan 8.7 USD.
 *  Trendyol: tedarikci verisindeki (TR) kargo. */
export function productShipping(product: ProductCategory | null | undefined, isEtsy: boolean): number {
  if (isEtsy) return product?.shippingCostOverride ?? DEFAULT_ETSY_SHIPPING_USD;
  const s = product?.sizeOptions?.[0];
  const t = (isEtsy ? s?.byCountry?.US?.shippingTiers : s?.byCountry?.TR?.shippingTiers) ?? s?.shippingTiers;
  return t?.premium ?? t?.plus ?? t?.base ?? s?.shippingCost ?? 0;
}

/** Liste gorsellerinin SONUNA eklenecek aciklayici gorseller:
 *  1) urunun kullandigi secenek setlerinin chart'lari (orn. zincir uzunlugu
 *     tablosu — yalniz o secenek varsa), 2) sablonun TUM listelerde olacak
 *  gorselleri. Ayni gorsel iki kez eklenmez. */
export function trailingImages(template: TemplateRow | null | undefined, chartImages: string[]): string[] {
  return [...new Set([...chartImages, ...(template?.imageUrls ?? []).filter(Boolean)])];
}

/** Zincir uzunlugu seti mi (Zincir Uzunlugu, Chain Length, Length, "Lengh") */
export const isChainLengthSet = (set: { name: string; etsyName?: string | null }) =>
  [set.name, set.etsyName ?? ""].some((n) => /zincir\s*uzunlu|chain\s*length|^\s*leng(th|h)\s*$/i.test(n));

/** Turun zincir uzunlugu kurali (inch). Aciklamaya tek basina guvenilmez:
 *  kolyede en kucuk 12, bileklik en fazla 5'ten (3.5 da olabilir) baslar.
 *  Aciklamadan okunan deger araliga uymazsa atilir; iki degerden az kalirsa
 *  turun varsayilan listesi kullanilir. */
const CHAIN_LENGTH_RULES: Record<string, { min: number; max: number; maxStart: number; defaults: number[] }> = {
  kolye: { min: 12, max: 36, maxStart: 18, defaults: [12, 14, 16, 18, 20, 22, 24] },
  bileklik: { min: 3.5, max: 11, maxStart: 5, defaults: [5, 6, 7, 8, 9, 10] },
};

/** Tedarikci aciklamasindaki "Zincir Uzunluğu: 5-6-7-8-9-10 inch" (inch sayilari) */
function descriptionChainLengths(product: ProductCategory | null | undefined): number[] {
  const src = product?.source;
  const text = [src?.descriptionText, descriptionPlainText(src?.descriptionHtml)].filter(Boolean).join("\n");
  const m = text.match(/zincir\s*uzunlu[ğg]u\s*:?\s*((?:\d+(?:[.,]\d+)?\s*(?:[-–\/,]|ve)?\s*)+)\s*(inch|inç|in\b|"|cm)/i);
  if (!m) return [];
  const cm = /cm/i.test(m[2]);
  const nums = m[1].split(/[-–\/,]|\sve\s|\s+/).map((x) => Number(x.replace(",", "."))).filter((n) => n > 0 && n < 200);
  // cm verildiyse inch'e cevrilip yarim inch'e yuvarlanir
  return [...new Set(nums.map((n) => (cm ? Math.round((n / 2.54) * 2) / 2 : n)))].sort((x, y) => x - y);
}

/** Urunun zincir uzunluklari ("5 inches"...) — turune gore (kolye/bileklik).
 *  Rexven eksende yalnizca secili degeri veriyor (3,5 inches); liste aciklamadan
 *  okunur, turun araligina gore dogrulanir. Kolye/bileklik disi: null (sablon seti). */
export function productChainLengths(product: ProductCategory | null | undefined): string[] | null {
  const type = productJewelryType(product as never);
  const rule = type ? CHAIN_LENGTH_RULES[type] : undefined;
  if (!rule) return null;
  const fromDesc = descriptionChainLengths(product).filter((n) => n >= rule.min && n <= rule.max);
  const ok = fromDesc.length >= 2 && fromDesc[0] <= rule.maxStart;
  return (ok ? fromDesc : rule.defaults).map((n) => `${n} inches`);
}

/** Sablonun secenek setleri: varyasyon (Etsy en fazla 3 eksen), kisisellestirme, chart gorselleri.
 *  product verilirse zincir uzunlugu setinin degerleri urun turune gore
 *  (kolye 12+, bileklik 3.5/5+) ve aciklamaya gore degistirilir. */
export function resolveOptionSets(refs: OptionRef[], optionSets: OptionSet[], product?: ProductCategory | null) {
  const lengths = productChainLengths(product);
  const refWithSet = refs
    .map((r) => {
      const set = optionSets.find((o) => o.id === r.optionSetId);
      // Urunun turune ait set (ör. [bileklik] Zincir Uzunlugu) kendi degerleriyle
      // kullanilir; tur kurali yalnizca ture bagli olmayan (genel) sette devreye girer
      const setForThisType = !!set?.productTypeKey && set.productTypeKey === productJewelryType(product as never);
      if (set && lengths && isChainLengthSet(set) && !setForThisType) {
        const setLabels = new Set(set.values.map((v) => v.label.toLowerCase()));
        // Setin cetveli (kolye uzunluk gorseli) urunun uzunluklarini kapsamiyorsa eklenmez
        const chartFits = lengths.every((l) => setLabels.has(l.toLowerCase()));
        // Urunde elle daraltilmis degerler varsa (katalog secenekleri) onlara uyulur
        const picked = r.values?.length ? lengths.filter((l) => r.values!.some((v) => v.toLowerCase() === l.toLowerCase())) : [];
        const values = (picked.length ? picked : lengths).map((label) => ({ label }));
        return { ref: r, set: { ...set, values, chartImageUrl: chartFits ? set.chartImageUrl : null } };
      }
      // Urunun desteklemedigi degerler listede cikmasin
      const filtered = set && r.values?.length ? { ...set, values: set.values.filter((v) => r.values!.includes(v.label)) } : set;
      return { ref: r, set: filtered };
    })
    .filter((x): x is { ref: OptionRef; set: OptionSet } => !!x.set);
  return {
    refWithSet,
    // Kesme YOK: eskiden ilk 2 set alinip 3.'su sessizce atiliyordu — 9 urunde (23 ilan) zincir
    // uzunlugu/stil secenegi listeye hic girmedi, aciklama "uzunluk secin" dedigi halde (REX-288,
    // 26/10). Etsy 3 ekseni kabul ediyor; 3'u asarsa yayin oncesi kontrol acik hata verir.
    variationSets: refWithSet.filter((x) => x.ref.role === "variation" && x.set.kind === "list" && !x.ref.chartOnly),
    personalizationSets: refWithSet.filter((x) => x.ref.role === "personalization" && x.set.kind !== "chart" && !x.ref.chartOnly),
    // Chart gorselleri: secim sunmaz, yalnizca urun fotograflarinin SONUNA eklenir
    chartImages: refWithSet.map((x) => x.set.chartImageUrl?.trim()).filter((u): u is string => !!u),
  };
}

type AxisMeta = {
  axisRoles?: Record<string, string>; axisLabels?: Record<string, string>; axisTitles?: Record<string, string>;
  /** Degerin Etsy etiketi (eklentide/AI ile belirlenen) */
  axisValueLabels?: Record<string, Record<string, string>>;
};
const axisMeta = (p: ProductCategory | null | undefined) => (p as unknown as AxisMeta | null) ?? {};

/** Musteriye gorunen deger adi: "3" yerine "3 Hearts".
 *  Kalipta {value} varsa oraya, yoksa kalip sona eklenir. */
export function axisValueLabel(product: ProductCategory | null | undefined, axisName: string, value: string): string {
  const exact = axisMeta(product).axisValueLabels?.[axisName]?.[value]?.trim();
  if (exact) return exact;
  const pattern = axisMeta(product).axisLabels?.[axisName]?.trim();
  if (!pattern) return value;
  return pattern.includes("{value}") ? pattern.replace("{value}", value) : `${value} ${pattern}`;
}

/** Taslaga yazilacak varyasyon eksenleri.
 *  1. yuva: urunun FIYAT ekseni (kac halka, kac tas, hangi stil) — fiyat
 *           yalnizca buna bagli, boylece kombinasyon tavani 2500 kalir
 *  2-3.   : sablondaki "varyasyon" rollu setler (renk, zincir uzunlugu) */
export function buildVariations(input: {
  product: ProductCategory | null | undefined;
  cost: number;
  /** Maliyete eklenen kargo: fiyat kurala (maliyet + kargo) uzerinden uygulanir */
  shipping?: number;
  rule: PriceRule;
  variationSets: Array<{ set: OptionSet }>;
  colors: string[];
}): PlannedVariation[] {
  const { product, cost, rule, variationSets, colors } = input;
  const shipping = input.shipping ?? 0;
  const out: PlannedVariation[] = [];
  const meta = axisMeta(product);
  const axes = (product?.source?.variationTypes ?? []) as Array<{ name: string; values: string[] }>;
  let priceAxis = axes.find((a) => meta.axisRoles?.[a.name] === "price") ?? null;
  // Fiyat ekseniyle AYNI eksen secenek setinden de geliyorsa (Zincir Uzunlugu)
  // iki kez eklenmez: fiyat gercekten degisiyorsa urunun ekseni (fiyatli),
  // degismiyorsa secenek seti (Etsy adi ve urune ozel degerlerle) kullanilir.
  // Birlesik fiyat ekseni ("Tas Sayisi + Pendant Sayisi") analizdeki "Style" setiyle adindan eslesmez:
  // katalogdaki Etsy basligi (axisTitles) setin Etsy adiyla ayniysa ayni eksendir
  const sameAxis = (set: OptionSet) => !!priceAxis && ([set.name, set.etsyName ?? ""].some((n) => n && optionValueMatch(n, priceAxis!.name))
    || (!!set.etsyName?.trim() && set.etsyName.trim().toLowerCase() === meta.axisTitles?.[priceAxis.name]?.trim().toLowerCase()));
  let setsToUse = variationSets;
  // Fiyat ekseninin karsiligi olan set: Etsy basligi ve degerlerin Etsy adlari ondan gelir.
  // Yoksa tedarikcinin ham ekseni ("Zincir Tipi", "Singapur Chain") musteriye gidiyordu (26/10)
  let axisSet: OptionSet | null = null;
  if (priceAxis && variationSets.some((x) => sameAxis(x.set))) {
    if (productPriceVaries(product as never)) {
      axisSet = variationSets.find((x) => sameAxis(x.set))!.set;
      setsToUse = variationSets.filter((x) => !sameAxis(x.set));
    } else priceAxis = null;
  }

  // Ucretli ikinci baski yuzu (Ön-Arka İki Yüz): alici yazisi yoksa (arka yazi ek ucreti tanimli degil)
  // desen arkaya da basilir; secim fiyat eksenine birlesir — 3 varyasyon yuvasi dolu olabilir (REX-245, 31/10)
  const extraSide = product?.source?.printType && !(product.optionRefs ?? []).some((r) => r.addOnCost)
    ? (product.source.printSides ?? []).find((s) => s !== product.source!.printType) : undefined;
  const tierCost = (t?: { premium?: number; plus?: number; base?: number }) => t?.premium ?? t?.plus ?? t?.base;
  const sideCost = (so: NonNullable<ProductCategory["sizeOptions"]>[number] | undefined) => (extraSide && so ? tierCost(so.printCosts?.[extraSide]) : undefined);
  const sideOn = !!extraSide && (product?.sizeOptions ?? []).some((so) => (sideCost(so) ?? 0) > (tierCost(so.costTiers) ?? so.cost ?? 0) + 0.009);

  if (priceAxis) {
    // Fiyatlar katalogtan: her deger bir sizeOption satiri
    const bySize = new Map((product?.sizeOptions ?? []).map((so) => [so.size, so]));
    const sidesAxis = /y[uü]z|side|bask/i.test(priceAxis.name);
    const name = meta.axisTitles?.[priceAxis.name]?.trim() || axisSet?.etsyName?.trim() || priceAxis.name;
    out.push({
      // Etsy'de gorunen baslik: katalogta tanimliysa o (Number of Hearts)
      name: sideOn && !sidesAxis ? `${name} + Print Side` : name,
      options: priceAxis.values.flatMap((v, i) => {
        const so = bySize.get(v);
        const c = so ? (so.costTiers?.premium ?? so.costTiers?.plus ?? so.costTiers?.base ?? so.cost ?? 0) : cost;
        // Etiket musteri diliyle yazilir; fiyat eslemesi ham degere gore kalir
        // Elle verilen Etsy etiketi yoksa ayni sayida degerli setin etiketi (sirayla)
        const exact = meta.axisValueLabels?.[priceAxis!.name]?.[v]?.trim();
        const fromSet = axisSet && axisSet.values.length === priceAxis!.values.length ? axisSet.values[i]?.label : undefined;
        const label = exact || fromSet || axisValueLabel(product, priceAxis!.name, v);
        const back = sideOn && !sidesAxis ? sideCost(so) : undefined;
        return back == null
          ? [{ label, sellingPrice: rule.price(c + shipping) }]
          : [{ label: `${label}, Front Only`, sellingPrice: rule.price(c + shipping) }, { label: `${label}, Front & Back`, sellingPrice: rule.price(back + shipping) }];
      }),
    });
  } else if (sideOn && setsToUse.length < 3) {
    const so = product?.sizeOptions?.[0];
    const back = sideCost(so);
    if (back != null) out.push({ name: "Print Side", options: [
      { label: "Front Only", sellingPrice: rule.price(cost + shipping) },
      { label: "Front & Back", sellingPrice: rule.price(back + shipping) },
    ] });
  }

  for (const { set } of setsToUse) {
    // Kutuphanedeki Etsy adi (Finish, Length) varsa musteriye o gorunur
    out.push({ name: set.etsyName?.trim() || set.name, options: set.values.map((v) => ({ label: v.label, priceAdjust: 0 })) });
  }

  // Hicbiri yoksa eski davranis: renk ekseni
  if (out.length === 0 && colors.length > 0) {
    out.push({
      name: "Renk",
      options: colors.map((c) => ({
        label: c,
        priceAdjust: 0,
        sku: `${product?.source?.sku ?? product?.id}-${c.toUpperCase().replace(/\s+/g, "")}`,
      })),
    });
  }
  return out;
}

/** Etsy kisisellestirme sorulari: setlerden + urunun isim alanlarindan */
/** Dogum tasi seti mi (Doğum Taşı, Birthstone) — tas SAYISI ekseni degil */
const isBirthstoneSet = (set: { name: string; etsyName?: string | null }) =>
  [set.name, set.etsyName ?? ""].some((n) => /do[gğ]um\s*ta[sş]|birthstone/i.test(n) && !/say[iı]|number|count|adet/i.test(n));

/** Urunde birden fazla tas secilebiliyor mu: tas sayisi ekseni (Doğum Taşı Sayısı,
 *  Number of Stones) birden fazla degerli */
function hasMultiStoneAxis(product: ProductCategory | null | undefined): boolean {
  const meta = axisMeta(product);
  const axes = (product?.source?.variationTypes ?? []) as Array<{ name: string; values: string[] }>;
  return axes.some((a) => a.values.length > 1
    && [a.name, meta.axisTitles?.[a.name] ?? ""].some((n) => /ta[sş][iı]?\s*say|stones?.*(number|count)|(number|count).*stones?|stone\s*count/i.test(n)));
}

export function buildPersonalization(
  product: ProductCategory | null | undefined,
  personalizationSets: Array<{ ref: OptionRef; set: OptionSet }>,
  /** Ek maliyet -> Etsy ek ucreti (satis fiyati farki). Verilmezse ek ucret yazilmaz. */
  addOnPrice?: (cost: number) => number,
): { enabled: true; questions: Array<Record<string, unknown>> } | null {
  const questions: Array<Record<string, unknown>> = [];

  // Urunun kendi isim/harf alanlari (katalogta tanimli)
  const perso = (product as unknown as { personalization?: { enabled: boolean; slots: number; font?: string } } | null)?.personalization;
  if (perso?.enabled) {
    for (let i = 0; i < perso.slots; i++) {
      questions.push({
        question_type: "text_input",
        question_text: perso.slots > 1 ? `Isim ${i + 1}` : "Isim",
        required: true,
        max_allowed_characters: 20,
        ...(perso.font ? { instructions: `Yazi tipi: ${perso.font}` } : {}),
      });
    }
  }

  const multiStone = hasMultiStoneAxis(product);
  // Urune ayrica dogum tasi METIN alani eklendiyse liste seti ikinci kez sorulmaz
  const hasBirthstoneText = personalizationSets.some((x) => x.set.kind === "text" && isBirthstoneSet({ name: x.set.name.replace(/ay(lar)?[iı]?/gi, ""), etsyName: x.set.etsyName }));
  for (const { ref, set } of personalizationSets) {
    // Coklu tasli urunde tek acilir liste yetmez (Etsy'de soru varyasyona gore
    // cogalamaz, en fazla 5 soru): aylar sirayla tek metin alanina yazilir.
    // Dogum tasi tablosu gorseli listede kalir.
    if (multiStone && set.kind === "list" && isBirthstoneSet(set)) {
      if (hasBirthstoneText) continue;
      questions.push({
        question_type: "text_input",
        question_text: "Birthstone Months",
        instructions: "Enter one birthstone month per stone, in order (e.g. Jan, May, Oct).",
        required: ref.required !== false,
        max_allowed_characters: 100,
      });
      continue;
    }
    if (set.kind === "list") {
      questions.push({
        question_type: "dropdown",
        // Aliciya gorunen soru: setin Etsy adi ("Font"); panel adi ("Font1") degil
        question_text: (set.etsyName?.trim() || set.name).slice(0, 45),
        required: ref.required !== false, // varsayilan zorunlu; yalnizca acikca kapatilirsa istege bagli
        // Etsy dropdown siniri: 30 secenek, etiket 20 karakter
        options: set.values.slice(0, 30).map((v) => ({ label: v.label.slice(0, 20) })),
      });
    } else if (set.kind === "text") {
      questions.push({
        question_type: "text_input",
        // Etsy adi = aliciya gorunen soru; not = yonerge
        question_text: (set.etsyName?.trim() || set.name).slice(0, 45),
        ...(set.note?.trim() ? { instructions: set.note.trim().slice(0, 250) } : {}),
        required: ref.required !== false, // varsayilan zorunlu; yalnizca acikca kapatilirsa istege bagli
        max_allowed_characters: set.maxChars ?? 50,
        // Ucretli ek (arka yuz): Etsy yalniz istege bagli metin sorusunda kabul eder
        ...(ref.required === false && ref.addOnCost && addOnPrice ? { add_on_price: addOnPrice(ref.addOnCost) } : {}),
      });
    } else {
      questions.push({
        question_type: "unlabeled_upload",
        question_text: (set.etsyName?.trim() || set.name).slice(0, 45),
        required: ref.required !== false, // varsayilan zorunlu; yalnizca acikca kapatilirsa istege bagli
        max_allowed_files: 1,
      });
    }
  }

  return questions.length > 0 ? { enabled: true, questions: questions.slice(0, 5) } : null;
}

/** Listede kullanilacak secenekler: urune tanimliysa urunun, yoksa sablonun */
export function effectiveOptionRefs(product: ProductCategory | null | undefined, template: TemplateRow | null | undefined): OptionRef[] {
  const own = (product as unknown as { optionRefs?: OptionRef[] } | null)?.optionRefs;
  return own ?? ((template?.optionRefs ?? []) as OptionRef[]);
}

/** Etsy sinirlari: 3 varyasyon, 2500 kombinasyon, 5 kisisellestirme alani */
export function etsyLimitError(variations: PlannedVariation[], questionCount: number): string | null {
  const combos = variations.reduce((n, v) => n * v.options.length, 1);
  if (variations.length > 3) return "Etsy en fazla 3 varyasyon kabul ediyor — şablondaki seçenekleri azaltın.";
  if (combos > 2500) return `${combos} kombinasyon — Etsy sınırı 2500.`;
  if (questionCount > 5) return "Etsy en fazla 5 kişiselleştirme alanı kabul ediyor.";
  return null;
}

// ── Aciklama alt metni ────────────────────────────────────────────────
// Sabit bolumler (paket, hediye notu, soru) tum urunlerde ayni; siparis
// adimlari, malzeme, renkler ve zincir uzunlugu URUNDEN doldurulur. Boylece
// kolye/kupe/yuzuk/bileklik icin tek sablon yeter.

/** Varsayilan alt metin. Yer tutucular:
 *  {siparis} {malzeme} {renkler} {uzunluk_bolumu} {tur} {urun} */
export const DEFAULT_DESCRIPTION_FOOTER = `★★ H O W - T O - O R D E R ★★

{siparis}
• Please let us know if you need help with your order.

★★ M A T E R I A L ★★

{malzeme}

★★ F I N I S H ★★

{renkler}

{uzunluk_bolumu}Designed to catch the light and leave a gleaming impression! Energetic, unique and wonderfully dynamic — full of personality, just like you. A glorious gift or a personal treat for yourself. :)

★★ P A C K A G E ★★

• A stylish gift box is available — select the gift box option (additional fee).

★★ G I F T - N O T E ★★

• Want to include a short gift note? Select the gift note option (additional fee) and leave your message at checkout — we will add a handwritten note to your order.

★★ B E S T - G I F T ★★

• A special everyday {tur} for your mother, lover, girlfriend, best friend, grandma, daughter, wife or family — perfect for Birthday, Graduation, Anniversary, Christmas, Thanksgiving, Weddings and Valentine's Day.

★★ H A V E - A - Q U E S T I O N ? ★★

• Feel free to message us with any question or problem you may have during or after your order.

Thank you for choosing our jewelry! We are confident you will love the style, comfort and quality. Add a touch of elegance to your everyday look with our {urun}.`;

/** Urun turu (Ingilizce): ad ya da tur anahtarindan */
function jewelryNoun(product: ProductCategory | null | undefined): string {
  const key = String((product as unknown as { productTypeKey?: string } | null)?.productTypeKey ?? "");
  const name = `${key} ${product?.name ?? ""}`.toLowerCase();
  if (/earring|küpe|kupe/.test(name)) return "earrings";
  if (/bracelet|bileklik/.test(name)) return "bracelet";
  if (/anklet|halhal/.test(name)) return "anklet";
  if (/\bring\b|yüzük|yuzuk/.test(name)) return "ring";
  return "necklace";
}

const FINISH_RE = /finish|renk|color|colour|kaplama/i;
const LENGTH_RE = /length|lengh|uzunlu/i;

/** Alt metni urunun gercek varyasyon/kisisellestirme bilgisiyle doldurur */
export function renderDescriptionFooter(
  footer: string,
  product: ProductCategory | null | undefined,
  variations: Array<{ name: string; options: Array<{ label: string }> }>,
  personalization: { questions: Array<Record<string, unknown>> } | null,
): string {
  const noun = jewelryNoun(product);
  // Siparis adimlari: her varyasyon ekseni + kisisellestirme
  const steps = variations.map((v) => {
    const n = LENGTH_RE.test(v.name) ? `${noun} length` : FINISH_RE.test(v.name) ? "finish" : v.name.toLowerCase();
    return `• Choose the ${n}: ${v.options.map((o) => (/^rose$/i.test(o.label) ? "Rose Gold" : o.label)).join(", ")}`;
  });
  if (personalization?.questions.length) steps.push("• Fill in the personalization box (e.g. the name to engrave).");
  // Malzeme: 925 gumus + altin/rose kaplama notu (tedarikci: 925 uzerine 14 ayar kaplama)
  const mat = productAttributes(product);
  const rawMat = (mat["Materyal"] ?? mat["Material"] ?? mat["Malzeme"] ?? "").toLowerCase();
  const finishAxis = variations.find((v) => FINISH_RE.test(v.name));
  const finishes = finishAxis?.options.map((o) => o.label) ?? product?.colorAvailability?.map((c) => c.color) ?? [];
  const silver = /925|gümüş|gumus|silver/.test(rawMat);
  const materialLines = silver
    ? ["• 925 Sterling Silver", ...(finishes.some((f) => /gold|rose/i.test(f)) ? ["• Gold and Rose Gold finishes are 14K gold plated over 925 sterling silver"] : [])]
    : rawMat ? [`• ${rawMat}`] : [];
  const lengthAxis = variations.find((v) => LENGTH_RE.test(v.name));
  const lengthSection = lengthAxis
    ? `★★ C H A I N - L E N G T H ★★\n\n${lengthAxis.options.map((o) => `• ${o.label}`).join("\n")}\n\n`
    : "";
  // Urun adi: "Heartbeat ECG Pulse Necklace / Kalp Atisi…" -> Ingilizce kisim
  const english = (product?.name ?? noun).split(" / ")[0].trim();
  return footer
    .split("{siparis}").join(steps.join("\n"))
    .split("{malzeme}").join(materialLines.join("\n"))
    .split("{renkler}").join(finishes.map((f) => `• ${/^rose$/i.test(f) ? "Rose Gold" : f}`).join("\n"))
    .split("{uzunluk_bolumu}").join(lengthSection)
    .split("{tur}").join(noun)
    .split("{urun}").join(english)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ── Fiyat: en ucuz varyasyon ve liste fiyati ─────────────────────────
type PricedVar = { name: string; options: Array<{ label: string; sellingPrice?: number; priceAdjust?: number }> };

/** Kombinasyon fiyati (yayin ile ayni kural: fiyatli eksen tabani + farklar) */
function comboNormalPrice(basePrice: number, combo: Array<{ sellingPrice?: number; priceAdjust?: number }>): number {
  const explicit = combo.filter((o) => typeof o.sellingPrice === "number").map((o) => o.sellingPrice as number);
  const base = explicit.length ? Math.max(...explicit) : basePrice;
  const adjust = combo.filter((o) => typeof o.sellingPrice !== "number").reduce((s, o) => s + (Number(o.priceAdjust) || 0), 0);
  return base + adjust;
}

/** En ucuz varyasyona uyan her kombinasyonun (indirimli) satis fiyati.
 *  Normal fiyattan kuralla (maliyet+kargo)'ya geri gidilip entry kuraliyla
 *  yeniden hesaplanir; boylece fiyat eksenli urunde her secenek kendi
 *  maliyetiyle ucuzlar. match TUM eksenleri icerir (Etsy'ye kombinasyon basina). */
export function entryComboPrices(
  variations: PricedVar[], basePrice: number, rule: PriceRule, entry: EntryVariant | null | undefined,
): Array<{ match: Record<string, string>; price: number; base: number }> {
  if (!entry || !variations.length) return [];
  const eRule = entryRule(entry);
  const keys = Object.entries(entry.values).filter(([k, v]) => v && variations.some((x) => x.name === k));
  if (!keys.length) return [];
  const out: Array<{ match: Record<string, string>; price: number; base: number }> = [];
  const walk = (i: number, picked: PricedVar["options"]) => {
    if (out.length > 400) return;
    if (i === variations.length) {
      const match: Record<string, string> = {};
      picked.forEach((o, j) => { match[variations[j].name] = o.label; });
      if (!keys.every(([k, v]) => match[k] === v)) return;
      const base = rule.base(comboNormalPrice(basePrice, picked)); // maliyet + kargo
      out.push({ match, base, price: eRule.price(base) });
      return;
    }
    for (const o of variations[i].options) walk(i + 1, [...picked, o]);
  };
  walk(0, []);
  return out;
}

/** Varyasyon fiyatlarini carpar (Etsy liste fiyati icin x2) */
export function scaleVariationPrices<T extends PricedVar>(variations: T[], factor: number): T[] {
  const r = (n: number) => Math.round(n * factor * 100) / 100;
  return variations.map((v) => ({
    ...v,
    options: v.options.map((o) => ({
      ...o,
      ...(typeof o.sellingPrice === "number" ? { sellingPrice: r(o.sellingPrice) } : {}),
      ...(typeof o.priceAdjust === "number" && o.priceAdjust !== 0 ? { priceAdjust: r(o.priceAdjust) } : {}),
    })),
  }));
}

/** Ilhamlarda tek basina en sik gecen kategori; esitlikte ya da ilham yoksa null (kullanici secer) */
export function soleLeadingInspiration(inspirations: ProductInspiration[]): ProductInspiration | null {
  const count = new Map<number, number>();
  for (const x of inspirations) if (x.taxonomyId) count.set(x.taxonomyId, (count.get(x.taxonomyId) ?? 0) + 1);
  const top = Math.max(0, ...count.values());
  const leaders = [...count].filter(([, n]) => n === top);
  return leaders.length === 1 ? inspirations.find((x) => x.taxonomyId === leaders[0][0]) ?? null : null;
}

const TYPE_TAXONOMY: Record<string, string> = { kolye: "Necklaces", kupe: "Earrings", yuzuk: "Rings", bileklik: "Bracelets", halhal: "Anklets", set: "Jewelry Sets" };

/** Ilhamsiz urun icin secenek: taki turunun Etsy dalindaki kategoriler (kategori basina bir kayit) */
export function sameTypeTaxonomies(inspirations: ProductInspiration[], typeKey: string | null): ProductInspiration[] {
  const branch = typeKey ? TYPE_TAXONOMY[typeKey] : undefined;
  if (!branch) return [];
  const byId = new Map(inspirations.filter((x) => x.taxonomyId && x.taxonomyPath?.startsWith(`Jewelry > ${branch}`)).map((x) => [x.taxonomyId, x]));
  return [...byId.values()].sort((a, b) => (a.taxonomyPath ?? "").localeCompare(b.taxonomyPath ?? ""));
}
